import { useEffect, useState } from 'react';
import { Button, Textfield } from '@flexo/atoms';
import {
  IconWrapper,
  REGEX,
  getPostAuthRedirectUrl,
} from '@flexo/general';
import styles from './web-log-in.module.scss';
import { Player } from '@lottiefiles/react-lottie-player';
import { useTranslation } from 'react-i18next';
import { ALERT, USER } from '@flexo/reducers';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFormValidation, WEB_ALLOWED_ROLES } from '@flexo/hooks';
import { useSignIn, useAuth, useUser, useClerk } from '@clerk/clerk-react';

const ENV = (import.meta as any).env;

/* eslint-disable-next-line */
export interface WebLogInProps {
  logInData: any;
  setLogInFlow: any;
  bgColor?: string;
  /** FLEXO-6063: when true, show the community member registration link. */
  showRegister?: boolean;
}

export function WebLogIn({ setLogInFlow, showRegister }: WebLogInProps) {
  const { LogIn__Inputs, LogIn__Footer, LogIn__Footer__Button } = styles;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { signIn, isLoaded: isSignInLoaded, setActive } = useSignIn();
  const { signOut, isSignedIn, isLoaded: isAuthLoaded } = useAuth();
  const { user: clerkUser } = useUser();
  const clerk = useClerk();

  const [authData, setAuthData] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    return {
      email: emailParam || '',
      password: '',
      createPassword: '',
    };
  });

  const [logInClicked, setLogInClicked] = useState(false);
  const [logInError, setLogInError] = useState(false);
  const [networkStatus, setNetworkStatus] = useState(false);
  const { t } = useTranslation();

  const [inputType, setInputType] = useState({
    email: 'email',
    password: 'password',
    confirmPassword: 'password',
  });

  // Email validation function
  const isValidEmail = (email: string) => {
    return REGEX.email.test(email);
  };

  // Form validation hook - only validate email for login
  const {
    errors,
    blur,
    handleBlur,
    handleFocus,
  } = useFormValidation(
    {
      fields: ['email'], // Only validate email, not password
      validationRules: {
        email: {
          validate: (value) => isValidEmail(value),
        },
      },
      hasPasswordFields: false,
    },
    authData
  );

  const { title, inputs, buttonLabel } = t('pages.login.data', {
    returnObjects: true,
  }) as any;

  const loader = '/loaderLottie.json';

  // Check if user was redirected after password or email update
  useEffect(() => {
    const passwordUpdatedLocal = localStorage.getItem('passwordUpdated');
    const passwordUpdatedSession = sessionStorage.getItem('passwordUpdated');
    const emailUpdatedLocal = localStorage.getItem('emailUpdated');
    const emailUpdatedSession = sessionStorage.getItem('emailUpdated');
    const showInviteAlreadyActivated = sessionStorage.getItem('showInviteAlreadyActivated');
    const showUserAlreadyActive = sessionStorage.getItem('showUserAlreadyActive');

    if (passwordUpdatedLocal === 'true' || passwordUpdatedSession === 'true') {
      localStorage.removeItem('passwordUpdated');
      sessionStorage.removeItem('passwordUpdated');

      dispatch(ALERT.ResetAlert());
      dispatch(
        ALERT.SetAlert({
          value: {
            type: 'BANNER_PASSWORD_RESET_SUCCESS',
          },
        } as any)
      );
    } else if (emailUpdatedLocal === 'true' || emailUpdatedSession === 'true') {
      localStorage.removeItem('emailUpdated');
      sessionStorage.removeItem('emailUpdated');

      dispatch(ALERT.ResetAlert());
      dispatch(
        ALERT.SetAlert({
          value: {
            type: 'BANNER_CONFIRM_CREDENTIALS_CHANGES_SUCCESS',
          },
        } as any)
      );
    } else if (showInviteAlreadyActivated === 'true') {
      // Remove the flag from sessionStorage
      sessionStorage.removeItem('showInviteAlreadyActivated');

      // Show invite already activated error message
      dispatch(
        ALERT.SetAlert({
          payload: {
            value: {
              type: 'BANNER_INVITE_ALREADY_ACTIVATED',
            },
          },
        } as any)
      );
    } else if (showUserAlreadyActive === 'true') {
      // Remove the flag from sessionStorage
      sessionStorage.removeItem('showUserAlreadyActive');
      const message = sessionStorage.getItem('userAlreadyActiveMessage');
      if (message) {
        sessionStorage.removeItem('userAlreadyActiveMessage');
      }

      // Show user already active message
      dispatch(
        ALERT.SetAlert({
          payload: {
            value: {
              type: 'BANNER_USER_ALREADY_ACTIVE',
              message: message || 'Questo utente è già attivo. Si prega di eseguire il login.',
            },
          },
        } as any)
      );
    }
  }, [dispatch]);

  const handleInputChange = (event: any) => {
    setAuthData({ ...authData, [event.target.name]: event.target.value });
  };

  // Check if user is already authenticated when component mounts
  // If email query param differs from logged-in user, sign out first
  useEffect(() => {
    if (!isAuthLoaded) return;

    if (isSignedIn && clerkUser) {
      const params = new URLSearchParams(window.location.search);
      const emailParam = params.get('email');

      if (emailParam) {
        const currentEmail = clerkUser.primaryEmailAddress?.emailAddress;
        if (currentEmail && emailParam.toLowerCase() !== currentEmail.toLowerCase()) {
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          signOut(() => {});
          return;
        }
      }

      // Same email or no email param - redirect to app.
      // FLEXO-6112 (#4): replace (not push) so the `/login?email=...` entry the
      // invite flow leaves behind is not reachable via Back once authenticated.
      // That stale entry was the precondition for the "language switch / drawer
      // Esc drops the session" bug: a stray history.back() landed on it and the
      // mount check below signed the user out.
      const postAuthRedirectUrl = getPostAuthRedirectUrl();
      if (postAuthRedirectUrl) {
        // Cross-domain (prod) reloads onto FIRST_PAGE; app.tsx's session effect
        // then routes an end user on to their own member home after the reload.
        window.location.href = postAuthRedirectUrl;
      } else {
        // FLEXO-6112 (#3): send end users to their own member home, not the staff
        // FIRST_PAGE (/communities) which 403s for them. Staff keep FIRST_PAGE.
        const orgMemberships = clerkUser.organizationMemberships || [];
        const targetOrgId = ENV.VITE_APP_CLERK_ORG_ID;
        const orgMembership = targetOrgId
          ? orgMemberships.find((m: any) => m.organization.id === targetOrgId) ||
            orgMemberships[0]
          : orgMemberships[0];
        const orgRole = orgMembership?.role;
        const flexoUserId = clerkUser.publicMetadata?.flexo_user_id;
        const target =
          orgRole === 'org:end_user' && flexoUserId
            ? `/member/${flexoUserId}`
            : `/${ENV.VITE_APP_FIRST_PAGE}`;
        navigate(target, { replace: true });
      }
    }
  }, [isAuthLoaded, isSignedIn, clerkUser, signOut, navigate]);

  const handleSignIn = async () => {
    if (logInClicked || !isSignInLoaded) return;

    // Capture redirect URL BEFORE any async work — React Router may strip query params
    const capturedPostAuthRedirectUrl = getPostAuthRedirectUrl();

    setLogInClicked(true);
    setLogInError(false);

    try {
      // CRITICAL FIX: Check if there's already an active session
      // If so, sign out first to avoid "identifier_already_signed_in" error
      const existingSessionId = signIn?.client?.sessions?.find(
        (s: any) => s.status === 'active'
      )?.id;

      if (existingSessionId) {
        try {
          await signOut();
          // Wait a moment for Clerk to fully clear the session
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch (signOutError) {
          // Continue with sign-in attempt anyway
        }
      }

      // Sign in with Clerk
      const result = await signIn.create({
        identifier: authData.email,
        password: authData.password,
      });

      if (result.status === 'complete') {
        // Set the active session
        await setActive({ session: result.createdSessionId });
        
        // After setActive, access user via useClerk() which is always up-to-date
        // (signIn?.client?.sessions is unreliable after setActive)
        await new Promise(resolve => setTimeout(resolve, 300));

        const activeUser = clerk.user;

        const orgMemberships = activeUser?.organizationMemberships || [];
        const targetOrgId = ENV.VITE_APP_CLERK_ORG_ID;
        const orgMembership = targetOrgId
          ? orgMemberships.find((m: any) => m.organization.id === targetOrgId) || orgMemberships[0]
          : orgMemberships[0];
        const orgRole = orgMembership?.role;

        // Check role - must be one of the web-allowed roles (admin, supervisor, end_user).
        // IMPORTANT: Only block if we can confirm the user has a role that's NOT allowed.
        // If we can't get the role data, let them proceed (app.tsx will handle it).
        if (orgRole && !WEB_ALLOWED_ROLES.includes(orgRole)) {
          console.error('[WebLogIn] User does not have required role:', orgRole);
          // Clear the session that was just created
          try {
            await setActive({ session: null });
            await signOut();
          } catch (clearError) {
            console.error('[WebLogIn] Error clearing session:', clearError);
          }
          
          dispatch(
            ALERT.SetAlert({
              payload: {
                value: {
                  type: 'BANNER_NOT_ALLOWED',
                },
              },
            } as any)
          );
          setAuthData({
            email: '',
            password: '',
            createPassword: '',
          });
          setLogInClicked(false);
          return;
        }
        
        // If we couldn't get the role (orgRole is undefined/null), let them proceed
        // app.tsx will do the proper role checking with the full user object

        // Set active organization (resolves pending 'choose-organization' task)
        if (orgMembership?.organization) {
          await clerk.setActive({ organization: orgMembership.organization.id });

          // Verify session transitioned from pending to active
          await new Promise(resolve => setTimeout(resolve, 300));
          const updatedSessionStatus = clerk.session?.status;
          if (updatedSessionStatus === 'pending') {
            try {
              await setActive({ session: null });
              await signOut();
            } catch (clearError) {
              console.error('[WebLogIn] Error clearing pending session:', clearError);
            }
            dispatch(
              ALERT.SetAlert({
                payload: { value: { type: 'BANNER_UNEXPECTED_ERROR' } },
              } as any)
            );
            setLogInClicked(false);
            return;
          }
        }

        // Redirect to satellite domain if configured
        if (capturedPostAuthRedirectUrl) {
          window.location.href = capturedPostAuthRedirectUrl;
          return;
        }
        // The app.tsx component will handle dispatching user data via Clerk hooks
        // No need to dispatch here as ClerkProvider will trigger the auth state change
      }
    } catch (error: any) {
      console.error('[WebLogIn] Clerk sign-in error:', error);
      
      // Handle different error types
      if (error.errors && error.errors.length > 0) {
        const errorCode = error.errors[0]?.code;
        
        // Handle "already signed in" / "session exists" errors
        if (errorCode === 'identifier_already_signed_in' || errorCode === 'session_exists') {
          try {
            await signOut();
            // Wait for sign out to complete
            await new Promise(resolve => setTimeout(resolve, 500));
            
            dispatch(
              ALERT.SetAlert({
                payload: {
                  value: {
                    type: 'BANNER_UNEXPECTED_ERROR',
                    message: 'Session conflict detected. Please try logging in again.',
                  },
                },
              } as any)
            );
          } catch (signOutError) {
            console.error('[WebLogIn] Failed to sign out after session conflict:', signOutError);
          }
        } else if (errorCode === 'form_identifier_not_found' || errorCode === 'form_password_incorrect') {
          dispatch(
            ALERT.SetAlert({
              payload: {
                value: {
                  type: 'BANNER_WRONG_LOGIN',
                },
              },
            } as any)
          );
        } else {
          dispatch(
            ALERT.SetAlert({
              payload: {
                value: {
                  type: 'BANNER_UNEXPECTED_ERROR',
                },
              },
            } as any)
          );
        }
      } else {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_UNEXPECTED_ERROR',
              },
            },
          } as any)
        );
      }

      setAuthData({ ...authData, password: '' });
      setLogInError(true);
    } finally {
      setLogInClicked(false);
    }
  };

  const togglePasswordVisibility = () => {
    setInputType((prevState) => ({
      ...prevState,
      password: prevState.password === 'password' ? 'text' : 'password',
    }));
  };

  const handleDescriptionClick = (inputName: string) => {
    if (inputName === 'password') {
      navigate('/forgot-password');
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();

      if (
        authData.password !== '' &&
        isValidEmail(authData.email) &&
        !logInClicked &&
        !logInError
      ) {
        handleSignIn();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [authData, logInClicked, logInError]);

  return (
    <div className={styles.LogIn}>
      <div className={styles.LogIn__Logo}>
        <IconWrapper iconName="logo_wide" />
      </div>
      <div className={styles.Login__Content}>
        <div className={styles.LogIn__Header__Title}>{title}</div>

        <div className={LogIn__Inputs}>
          {inputs?.map((input: any, input_i: number) => (
            <div
              key={input.name}
              className={input_i < inputs.length - 1 ? 'mb-2' : ''}
            >
              <Textfield
                textfieldColor="grey"
                variant="default"
                name={input.name}
                value={authData[input.name]}
                placeholder={input.placeholder}
                label={input.label}
                type={inputType[input.name]}
                error={
                  input.name === 'email'
                    ? (errors.email && blur.email) || logInError
                    : logInError
                }
                onChange={handleInputChange}
                onBlur={input.name === 'email' ? handleBlur : undefined}
                onFocus={(e) => {
                  if (input.name === 'email') handleFocus(e);
                  setLogInError(false);
                }}
                onPaste={(e) => false}
                onCopy={(e) => false}
                action={
                  input.name === 'password' ? input.description : undefined
                }
                onActionClick={
                  input.name === 'password'
                    ? () => handleDescriptionClick(input.name)
                    : undefined
                }
                errorDescription={
                  input.name === 'password' && logInError
                    ? input.descriptionError
                    : ''
                }
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                autoComplete={
                  input.name === 'email'
                    ? 'email'
                    : input.name === 'password'
                    ? 'current-password'
                    : 'off'
                }
                icon={
                  input.name === 'password'
                    ? inputType.password === 'password'
                      ? 'show'
                      : 'hide'
                    : ''
                }
                onIconClick={
                  input.name === 'password'
                    ? togglePasswordVisibility
                    : undefined
                }
              />
            </div>
          ))}
        </div>

        <div className={styles.LogIn__Actions}>
          {showRegister ? (
            <div className={styles.LogIn__Register}>
              <span>{t('pages.register.footer.label') as string}</span>{' '}
              <button
                type="button"
                className={styles.LogIn__Register__Link}
                onClick={() => navigate('/register')}
              >
                {t('pages.register.footer.button_label') as string}
              </button>
            </div>
          ) : null}

          <div className={LogIn__Footer}>
            <Button
              className={LogIn__Footer__Button}
              onClick={handleSignIn}
              label={
                logInClicked && !logInError ? (
                  <Player
                    autoplay
                    loop
                    src={loader}
                    style={{ height: 20, width: 60 }}
                  />
                ) : (
                  buttonLabel
                )
              }
              variant="primary"
              color="secondary"
              disabled={
                authData.password === '' ||
                !isValidEmail(authData.email) ||
                (logInClicked && !logInError) ||
                networkStatus ||
                (errors.email && blur.email)
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default WebLogIn;
